import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    HostBinding,
    Input,
    OnInit,
    output,
    ViewEncapsulation,
    EventEmitter,
    Injector,
} from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { FiBrowserEventsService } from '@fi-shared/fi-shared-services/fi-browser-events.service';
import { eventFindInPath, findClosestElement, isDefined, isEqual } from '@fi-shared/utils';
import { ITextItemMediaModel } from '@fi-shared/fi-text-item-helper/fi-text-item-media';
import { ITextItemLinkModel } from '@fi-shared/fi-text-item-helper/fi-text-item-link';
import { FiTextItemMediaSettingsDialogComponent } from '@fi-app/utilities/insert-media/fi-text-item-media-panel-handler/fi-text-item-media-settings-dialog/fi-text-item-media-settings-dialog.component';
import { FiDisplayAs } from '@fi-shared/types/display-as';
import { FiMediaStorageService } from '@fi-shared/media-bank/fi-media-storage.service';
import { TextItemMediaClickActionType } from '@fi-shared/types/text-item-media-click-action-type';
import { takeUntil } from 'rxjs/operators';
import {
    F1,
    F2,
    F3,
    F4,
    F5,
    F6,
    F7,
    F8,
    F9,
    F10,
    F11,
    F12,
    CONTROL,
    ALT,
    SHIFT,
    META,
    PAUSE,
    ESCAPE,
    TAB,
    PRINT_SCREEN,
    CONTEXT_MENU,
    INSERT,
} from '@angular/cdk/keycodes';
import { PAGE_DOWN, PAGE_UP, END, HOME, DOWN_ARROW, UP_ARROW, LEFT_ARROW, RIGHT_ARROW } from '@angular/cdk/keycodes';
import { FiMediaPanelHandlerBase } from '@fi-app/utilities/insert-media/fi-media-panel-handler-base';
import { FiImageSize, FileType } from '@fi-shared/types';
import { MediaBankStorageType } from '@fi-shared/media-bank/media-bank-storage-type';
import { FiTextItemHelperService } from '@fi-shared/fi-text-item-helper/fi-text-item-helper.service';
import { getVimeoPreviewImgContainer } from '@fi-app/shared/text-editor/text-editor-html-element';

const IGNORE_KEYS = () => {
    return [
        F1,
        F2,
        F3,
        F4,
        F5,
        F6,
        F7,
        F8,
        F9,
        F10,
        F11,
        F12,
        CONTROL,
        ALT,
        SHIFT,
        META,
        PAUSE,
        ESCAPE,
        TAB,
        PRINT_SCREEN,
        CONTEXT_MENU,
        INSERT,
    ];
};

const NAVIGATION_KEYS = [PAGE_DOWN, PAGE_UP, END, HOME, DOWN_ARROW, UP_ARROW, LEFT_ARROW, RIGHT_ARROW];

@Component({
    selector: 'fi-text-item-media-panel-handler',
    template: `
        <fi-text-item-media-panel-link
            (attachLinkToTextItemMedia)="handleAttachLinkToTextItemMedia($event)"
            (clearLinkToTextItemMedia)="clearLinkToTextItemMedia()"
            [textItemMediaModel]="textItemMediaModel"
            [closeMenu$]="closeMenu$.asObservable()"
            [isInternalLinksAllowed]="isInternalLinksAllowed"
        >
        </fi-text-item-media-panel-link>
        <fi-text-item-media-panel-size
            [currentItemMediaSizeClass]="currentItemMediaSizeClass"
            (setSizeClass)="setSizeClass($event)"
            [closeMenu$]="closeMenu$.asObservable()"
        >
        </fi-text-item-media-panel-size>
        <fi-text-item-media-panel-alignment
            [currentItemMediaAlignmentClass]="currentItemMediaAlignmentClass"
            (setAlignmentClass)="setAlignmentClass($event)"
            [closeMenu$]="closeMenu$.asObservable()"
        >
        </fi-text-item-media-panel-alignment>
        @if (isMediaSettingsAllowed) {
            <fi-text-item-media-panel-settings
                (openSettingsDialog)="openSettingsDialog()"
                [closeMenu$]="closeMenu$.asObservable()"
            >
            </fi-text-item-media-panel-settings>
        }

        <button mat-icon-button class="fi-text-item-media-panel-action" (click)="deleteTextItemMedia()">
            <mat-icon><span class="fas fa-trash"></span></mat-icon>
        </button>
    `,
    styleUrls: ['./fi-text-item-media-panel-handler.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-text-item-media-panel-handler' },
    standalone: false,
})
export class FiTextItemMediaPanelHandlerComponent extends FiMediaPanelHandlerBase implements OnInit {
    @Input() editableElement: HTMLElement;
    @Input() isInternalLinksAllowed = true;
    @Input() isMediaSettingsAllowed = true;

    public setCaretVisibility = output<boolean>();
    public onVimeoVideoAttachment = output<void>();

    currentTextItemMedia$ = new BehaviorSubject<HTMLElement>(null);
    textItemMediaModel: ITextItemMediaModel;
    currentItemMediaSizeClass: string;
    currentItemMediaAlignmentClass: string;
    closeMenu$ = new Subject<void>();

    constructor(
        injector: Injector,
        private fiBrowserEventsService: FiBrowserEventsService,
        private cdr: ChangeDetectorRef,
        private fiTextItemHelperService: FiTextItemHelperService,
        private mediaStorageService: FiMediaStorageService,
    ) {
        super(injector);
    }

    @HostBinding('style.visibility') get panelVisibilityStyle() {
        return this.currentTextItemMedia$.value && this.isMediaDisplayType() ? 'visible' : 'hidden';
    }

    public ngOnInit(): void {
        this.setKeyDownListener();
        this.fiBrowserEventsService
            .getWindowEventSubject$('blur')
            .pipe(takeUntil(this.ngOnDestroy$))
            .subscribe(() => {
                if (
                    document.activeElement.matches('[ccm-text-item-media-model]') &&
                    findClosestElement(document.activeElement as HTMLElement, this.editableElement)
                ) {
                    const iframeElement = document.activeElement as HTMLElement;
                    this.setCurrentTextItemMediaData(iframeElement);
                }
            });
        this.fiBrowserEventsService
            .getWindowEventSubject$('click')
            .pipe(takeUntil(this.ngOnDestroy$))
            .subscribe(($event: any) => {
                const target: HTMLElement = $event.target;

                if (findClosestElement(target, this.elementRef.nativeElement)) {
                    // ignore click on panel
                    return;
                }

                if (
                    (target.matches('[ccm-text-item-media-model]') || this.isParentElementMatches(target)) &&
                    findClosestElement(target, this.editableElement)
                ) {
                    // ContenteditableModelDirective.handleMediaClick has been commented out, instead we should show this panel
                    this.setCurrentTextItemMediaData(target);
                } else if (!this.isIgnored(target)) {
                    this.currentTextItemMedia$.next(null);
                    this.toggleItemMediaBorderClass(false);
                    this.setCaretVisibility.emit(true);
                }
            });

        this.panelWidth = this.elementRef.nativeElement.clientWidth;
        this.currentTextItemMedia$.takeUntil(this.ngOnDestroy$).subscribe((mediaElement) => {
            this.updatePanelPosition(mediaElement);
        });
    }

    private setCurrentTextItemMediaData(element: HTMLElement) {
        this.currentTextItemMedia$.next(element);
        if (element !== this.lastTextItemMedia) {
            this.toggleItemMediaBorderClass(false);
        }

        const isVimeoPreviewImage = element.hasAttribute('fi-vimeo-video-preview-image');
        const isVimeoNotAvailableHandler = element.classList.contains('fi-vimeo-player-not-available-container');
        if (isVimeoPreviewImage || isVimeoNotAvailableHandler) {
            this.lastTextItemMedia = element.parentElement;
            this.setTextItemMediaModel(element.parentElement);
            this.setRange(element.parentElement);
        } else {
            this.lastTextItemMedia = element;
            this.setTextItemMediaModel(element);
            this.setRange(element);
        }
        this.setCaretVisibility.emit(false);
    }

    protected setRange(targetElement: HTMLElement) {
        const range = document.createRange();
        range.setStart(targetElement, 0);
        range.setEnd(targetElement, 0);
        document.getSelection().removeAllRanges();
        document.getSelection().addRange(range);
    }

    protected setKeyDownListener() {
        this.fiBrowserEventsService
            .getWindowEventSubject$('keydown')
            .pipe(takeUntil(this.ngOnDestroy$))
            .subscribe(($event: KeyboardEvent) => {
                const target: HTMLElement = <any>$event.target;
                // https://chainformation.atlassian.net/browse/FI-4053
                // in safari document.getSelection().focusNode is contenteditable, not media file
                if (this.currentTextItemMedia$.value && findClosestElement(target, this.editableElement)) {
                    if (IGNORE_KEYS().some((key) => $event.keyCode === key)) {
                        return;
                    }
                    if (!NAVIGATION_KEYS.some((key) => $event.keyCode === key)) {
                        const parentElement = this.currentTextItemMedia$.value.parentElement;
                        parentElement.removeChild(this.currentTextItemMedia$.value);
                    }
                    this.setCaretVisibility.emit(true);
                    this.toggleItemMediaBorderClass(false);
                    this.currentTextItemMedia$.next(null);
                    setTimeout(() => {
                        this.closeMenu$.next();
                    });
                }
            });
    }

    protected isIgnored(target: HTMLElement): boolean {
        const ignoreClasses = [
            '.cdk-overlay-backdrop',
            '.mat-dialog-container',
            '.cdk-overlay-pane',
            '.fi-text-editor-panel',
        ];
        return ignoreClasses.some((ignoreClass) => {
            return !!eventFindInPath({ target }, ignoreClass);
        });
    }

    protected isParentElementMatches(target: HTMLElement): boolean {
        return target && target.parentElement && target.parentElement.matches('[ccm-text-item-media-model]');
    }

    protected toggleItemMediaBorderClass(isBorderShown: boolean): void {
        super.toggleItemMediaBorderClass(isBorderShown);
        this.dispatchChangeEvent();
    }

    protected setTextItemMediaModel(element: HTMLElement): void {
        if (this.isTargetElementModel(element)) {
            this.textItemMediaModel = JSON.parse(element.getAttribute('ccm-text-item-media-model'));
        } else if (this.isParentElementModel(element)) {
            this.textItemMediaModel = JSON.parse(element.parentElement.getAttribute('ccm-text-item-media-model'));
        }
        if (
            this.textItemMediaModel.hasOwnProperty('linkTo') &&
            !this.textItemMediaModel.linkTo.hasOwnProperty('Value')
        ) {
            delete this.textItemMediaModel.linkTo;
        }
        if (this.isMediaDisplayType()) {
            this.toggleItemMediaBorderClass(true);
        } else {
            this.openSettingsDialog();
        }
        this.setCurrentTargetElementClasses(element);

        if (!this.textItemMediaModel.hasOwnProperty('onClickActionType')) {
            this.setDefaultModelOnClickActionType();
        }

        this.cdr.detectChanges();
    }

    protected isMediaDisplayType(): boolean {
        return this.textItemMediaModel && this.textItemMediaModel.displayAs === FiDisplayAs.Image.Id;
    }

    protected setCurrentTargetElementClasses(element: HTMLElement): void {
        if (this.isMediaDisplayType()) {
            if (!element.className.includes('fi-text-item-media-item-size')) {
                element.classList.add('fi-text-item-media-item-size-original');
            }
            if (!element.className.includes('fi-text-item-media-item-alignment')) {
                element.classList.add('fi-text-item-media-item-alignment-center');
            }
            element.classList
                .toString()
                .split(' ')
                .forEach((elementClass: string) => {
                    if (elementClass.includes('fi-text-item-media-item-size')) {
                        this.currentItemMediaSizeClass = elementClass;
                    } else if (elementClass.includes('fi-text-item-media-item-alignment')) {
                        this.currentItemMediaAlignmentClass = elementClass;
                    }
                });
        }
    }

    protected isTargetElementModel(element: HTMLElement): boolean {
        return element.matches('[ccm-text-item-media-model]');
    }

    protected isParentElementModel(element: HTMLElement): boolean {
        return element.parentElement.matches('[ccm-text-item-media-model]');
    }

    protected handleAttachLinkToTextItemMedia(linkToData: ITextItemLinkModel): void {
        const url: string = this.fiTextItemHelperService.getUrlForItemLinkModel(linkToData);
        this.textItemMediaModel.linkTo = linkToData;

        if (this.lastTextItemMedia.parentElement.nodeName.toLowerCase() === 'a') {
            this.updateAttachedLinkData(url);
        } else {
            this.attachLinkToTextItemMedia(url);
        }

        this.dispatchChangeEvent();
    }

    private updateAttachedLinkData(url: string): void {
        const parentNode = this.lastTextItemMedia.parentNode as HTMLAnchorElement;
        parentNode.href = url;
        this.updateTextItemMediaModel(this.lastTextItemMedia);
    }

    private attachLinkToTextItemMedia(url: string): void {
        const el: HTMLAnchorElement = document.createElement('a');
        this.lastTextItemMedia.parentElement.replaceChild(el, this.lastTextItemMedia);
        el.href = url;
        el.appendChild(this.lastTextItemMedia);
        this.updateTextItemMediaModel(this.lastTextItemMedia);
    }

    protected clearLinkToTextItemMedia(): void {
        delete this.textItemMediaModel.linkTo;
        const mediaElement = this.lastTextItemMedia;
        this.lastTextItemMedia.setAttribute('ccm-text-item-media-model', JSON.stringify(this.textItemMediaModel));
        const mediaElementParentNode = mediaElement.parentNode;
        if (mediaElementParentNode.nodeName.toLowerCase() === 'a') {
            // note: handle removing anchor element with multiple children
            // https://chainformation.atlassian.net/browse/FI-5362
            if (mediaElementParentNode.childNodes.length > 1) {
                const index = Array.prototype.indexOf.call(mediaElementParentNode.children, mediaElement);
                const textItemMediaWrapper: HTMLDivElement = document.createElement('div');

                mediaElementParentNode.childNodes.forEach((node) => {
                    textItemMediaWrapper.appendChild(node.cloneNode(true));
                });
                textItemMediaWrapper.replaceChild(this.lastTextItemMedia, textItemMediaWrapper.childNodes[index]);
                mediaElementParentNode.parentNode.replaceChild(textItemMediaWrapper, mediaElementParentNode);
            } else {
                mediaElementParentNode.parentNode.replaceChild(this.lastTextItemMedia, mediaElementParentNode);
            }
        }
        this.dispatchChangeEvent();
    }

    protected setSizeClass(newClassName: string): void {
        this.lastTextItemMedia.classList.replace(this.currentItemMediaSizeClass, newClassName);
        this.currentItemMediaSizeClass = newClassName;
        this.updatePanelPosition(this.lastTextItemMedia);
        this.dispatchChangeEvent();
        this.onVimeoVideoSizeChange();
    }

    protected setAlignmentClass(newClassName: string): void {
        this.lastTextItemMedia.classList.replace(this.currentItemMediaAlignmentClass, newClassName);
        this.currentItemMediaAlignmentClass = newClassName;
        this.updatePanelPosition(this.lastTextItemMedia);
        this.dispatchChangeEvent();
    }

    protected setDefaultModelOnClickActionType(): void {
        if (this.textItemMediaModel.displayAs === FiDisplayAs.Image.Id) {
            this.textItemMediaModel.onClickActionType = TextItemMediaClickActionType.EnlargedView;
        } else {
            this.textItemMediaModel.onClickActionType = TextItemMediaClickActionType.DownloadFile;
        }
    }

    protected openSettingsDialog(): void {
        const dialogData = {
            textItemMediaModel: Object.assign({}, this.textItemMediaModel),
            linkTextValue: this.currentTextItemMedia$.value.innerText,
        };
        this.advancedTextPanelMediaLinkService.setTextEditorActiveButtons({ attachments: true });
        this.fiDialogsService
            .custom({
                headerText: this.appTextService.get('settings'),
                yesButtonText: this.appTextService.get('done'),
                noButtonText: this.appTextService.get('common.cancel'),
                contentComponentRef: FiTextItemMediaSettingsDialogComponent,
                contentComponentData: dialogData,
                config: {
                    minWidth: '20%',
                    autoFocus: false,
                },
            })
            .onYesButtonClick(() => {
                if (
                    !isEqual(this.textItemMediaModel, dialogData.textItemMediaModel) ||
                    dialogData.textItemMediaModel !== this.currentTextItemMedia$.value?.innerText ||
                    this.textItemMediaModel.linkTo
                ) {
                    this.textItemMediaModel = Object.assign({}, dialogData.textItemMediaModel);
                    delete this.textItemMediaModel.linkTo;
                    this.currentTextItemMedia$.next(null);
                    if (this.textItemMediaModel.onClickActionType === TextItemMediaClickActionType.DownloadFile) {
                        this.createDownloadFileElement(dialogData.linkTextValue);
                    } else {
                        this.createEnlargedViewElement(dialogData.linkTextValue);
                    }

                    this.dispatchChangeEvent();
                }
            })
            .afterClosed(() =>
                this.advancedTextPanelMediaLinkService.setTextEditorActiveButtons({ attachments: false }),
            );
    }

    protected createDownloadFileElement(linkText?: string) {
        const currentElement =
            this.lastTextItemMedia.parentElement.nodeName.toLowerCase() === 'a'
                ? this.lastTextItemMedia.parentElement
                : this.lastTextItemMedia;
        const el: HTMLAnchorElement = document.createElement('a');

        if (this.textItemMediaModel.hasOwnProperty('linkTo')) {
            delete this.textItemMediaModel.linkTo;
        }

        el.href = this.mediaStorageService.getMediaFileUrlByFileName(
            this.textItemMediaModel.file.FullName,
            undefined,
            this.textItemMediaModel.file.ChangedOn,
        );
        el.target = '_blank';

        if (this.isMediaDisplayType()) {
            this.lastTextItemMedia.setAttribute('ccm-text-item-media-model', JSON.stringify(this.textItemMediaModel));
            this.lastTextItemMedia.setAttribute('template-item-text-image', this.textItemMediaModel.file.FullName);
            const newChild =
                this.lastTextItemMedia.nodeName.toLowerCase() !== 'img'
                    ? this.getNewImageElement()
                    : (this.lastTextItemMedia.cloneNode() as HTMLElement);
            el.appendChild(newChild);
            this.resetLastTextItemMedia();
        } else {
            el.setAttribute('ccm-text-item-media-model', JSON.stringify(this.textItemMediaModel));
            el.text = linkText ? linkText : this.textItemMediaModel.file.Name + this.textItemMediaModel.file.Extension;
        }

        currentElement.parentElement.replaceChild(el, currentElement);
    }

    protected createEnlargedViewElement(linkText?: string) {
        if (!this.isMediaDisplayType()) {
            const spanEl: HTMLSpanElement = this.getNewSpanElement(linkText);
            this.lastTextItemMedia.parentElement.replaceChild(spanEl, this.lastTextItemMedia);
        } else {
            if (this.textItemMediaModel.file.FileType === FileType.Image) {
                this.onElementChange(this.getNewImageElement());
            } else if (this.textItemMediaModel.file.StorageType === MediaBankStorageType.Vimeo) {
                this.onElementChange(this.getVimeoPreviewImage());
            } else if (this.textItemMediaModel.file.FileType === FileType.Movie) {
                this.onElementChange(this.getNewVideoElement());
            } else {
                this.lastTextItemMedia.setAttribute(
                    'ccm-text-item-media-model',
                    JSON.stringify(this.textItemMediaModel),
                );
                const itemMediaParentElement = this.lastTextItemMedia.parentElement;
                if (itemMediaParentElement.nodeName.toLowerCase() === 'a') {
                    const newChild = this.lastTextItemMedia.cloneNode() as HTMLElement;
                    itemMediaParentElement.parentElement.replaceChild(newChild, itemMediaParentElement);

                    this.resetLastTextItemMedia();
                }
            }
        }
    }

    private onElementChange(newElement) {
        this.lastTextItemMedia.parentElement.replaceChild(newElement, this.lastTextItemMedia);
        this.setCurrentTargetElementClasses(newElement);
        this.resetLastTextItemMedia();
        this.onVimeoVideoSizeChange();
    }

    private resetLastTextItemMedia() {
        this.lastTextItemMedia = null;
    }

    private onVimeoVideoSizeChange() {
        if (this.textItemMediaModel.file.FileType === FileType.Movie) this.onVimeoVideoAttachment.emit();
    }

    protected deleteTextItemMedia(): void {
        const imageContainerEl = this.lastTextItemMedia.parentElement;
        if (imageContainerEl) {
            if (imageContainerEl.hasAttribute('ccm-text-item-media-model')) {
                imageContainerEl.parentElement.removeChild(imageContainerEl);
            } else {
                imageContainerEl.removeChild(this.lastTextItemMedia);
            }
        }

        this.currentTextItemMedia$.next(null);
    }

    private updateTextItemMedia(element: HTMLElement): void {
        setTimeout(() => {
            this.currentTextItemMedia$.next(element);
            this.lastTextItemMedia = element;
        });
    }

    private getNewSpanElement(linkText?: string): HTMLSpanElement {
        const spanEl: HTMLSpanElement = document.createElement('span');
        spanEl.setAttribute('template-item-text-image', this.textItemMediaModel.file.FullName);
        spanEl.classList.add('BrandedColor');
        spanEl.setAttribute('ccm-text-item-media-model', JSON.stringify(this.textItemMediaModel));
        spanEl.innerText = linkText
            ? linkText
            : this.textItemMediaModel.file.Name + this.textItemMediaModel.file.Extension;
        return spanEl;
    }

    private getVimeoPreviewImage(): HTMLElement {
        const previewContainer = getVimeoPreviewImgContainer(
            this.mediaStorageService.getMediaFileUrlByFileName(
                this.textItemMediaModel.file.FullName,
                FiImageSize.Medium,
                this.textItemMediaModel.file.ChangedOn,
            ),
        );
        previewContainer.children[0].setAttribute('template-item-text-image', this.textItemMediaModel.file.FullName);
        previewContainer.setAttribute('ccm-text-item-media-model', JSON.stringify(this.textItemMediaModel));
        return previewContainer;
    }

    /*private getVimeoPreviewImage(): HTMLDivElement {
        const previewPicture = this.textItemMediaModel.file.vimeoPreviewPicture;
        const divElement: HTMLDivElement = document.createElement('div');
        divElement.classList.add('video-preview-image-container', 'playCircleIcon',
                'fi-text-item-media-item-size-original', 'fi-text-item-media-item-alignment-center');
        divElement.setAttribute('ccm-text-item-media-model', JSON.stringify(this.textItemMediaModel));
        divElement.setAttribute('contentedtable', 'false');
        const previewImageElement: HTMLImageElement = document.createElement('img');
        previewImageElement.setAttribute('src', previewPicture.link);
        previewImageElement.setAttribute('fi-vimeo-preview-image', '');
        previewImageElement.classList.add('video-preview-image');

        divElement.appendChild(previewImageElement);
        return divElement;
    }*/

    private getNewVideoElement(): HTMLVideoElement {
        const videoEl = document.createElement('video');
        videoEl.setAttribute('controls', 'true');
        videoEl.setAttribute(
            'src',
            this.mediaStorageService.getMediaFileUrlByFileName(
                this.textItemMediaModel.file.FullName,
                undefined,
                this.textItemMediaModel.file.ChangedOn,
            ),
        );
        videoEl.classList.add('fi-text-item-media-item-size-original', 'fi-text-item-media-item-alignment-center');
        videoEl.setAttribute('ccm-text-item-media-model', JSON.stringify(this.textItemMediaModel));
        return videoEl;
    }

    private getNewImageElement(): HTMLImageElement {
        const imgEl = document.createElement('img');
        imgEl.setAttribute('template-item-text-image', this.textItemMediaModel.file.FullName);
        imgEl.classList.add('fi-text-item-media-item-size-original', 'fi-text-item-media-item-alignment-center');
        imgEl.setAttribute('ccm-text-item-media-model', JSON.stringify(this.textItemMediaModel));
        imgEl.setAttribute('draggable', 'false');
        imgEl.setAttribute(
            'src',
            this.mediaStorageService.getMediaFileUrlByFileName(
                this.textItemMediaModel.file.FullName,
                FiImageSize.Medium,
                this.textItemMediaModel.file.ChangedOn,
            ),
        );
        return imgEl;
    }

    private updateTextItemMediaModel(element: HTMLElement): void {
        element.setAttribute('ccm-text-item-media-model', JSON.stringify(this.textItemMediaModel));
    }

    private dispatchChangeEvent(): void {
        this.editableElement.dispatchEvent(new Event('change'));
    }
}
